/**
 * Shared helpers for rendering questionnaire / property_info edit audit
 * entries.
 *
 *   summariseQuestionnaireAuditEntry(entry)
 *      → returns a compact one-line string suitable for the lab Audit
 *        log sidebar. Falls back to "no field changes" for no-op edits.
 *
 *   diffQuestionnaireAuditEntry(entry)
 *      → returns the raw change list (used by the full Edit history
 *        disclosure on the QuestionnaireEditPanel).
 *
 * Both helpers are pure, JSX-free, and work off the audit entry shape
 * the backend persists (`{action, before, after, customer_diff, ...}`).
 *
 * Field-label maps are intentionally narrow — only the questionnaire
 * fields that staff can actually edit show up in the diff. Anything
 * else stays under-the-hood and is rendered as the snake_case key.
 */

const FIELD_LABEL = {
  reason_for_testing: "reason for testing",
  reason_for_testing_other: "reason for testing (other)",
  primary_water_use: "primary water use",
  primary_water_use_other: "primary water use (other)",
  tanks: "water tanks",
  filtration: "filtration",
  uv: "UV treatment",
  calcite: "calcite filter",
  tank_cleaned_5y: "tank cleaned in last 5y",
  home_older_than_10y: "home older than 10y",
  experienced_issues: "experienced issues",
  notes: "intake notes",
};

const REASON_LBL = {
  routine: "Routine check",
  drinking_safety: "Drinking water safety",
  property_sale: "Property sale",
  rental: "Rental property",
  new_system: "New system",
  taste_odour_colour_staining: "Taste / odour / colour / staining",
  illness_health: "Illness or health concern",
  other: "Other",
};
const PWU_LBL = {
  drinking: "Drinking",
  household: "Household",
  irrigation: "Irrigation / garden",
  livestock: "Livestock",
  pool_spa: "Pool / spa",
  commercial: "Commercial",
  other: "Other",
};
const YN_LBL = { yes: "Yes", no: "No", unsure: "Unsure" };
const FILT_LBL = {
  whole_house: "Whole house",
  under_sink: "Under sink",
  both: "Both",
  none: "None",
  unsure: "Unsure",
};
const ISSUE_LBL = {
  bad_taste: "Bad taste", bad_smell: "Bad smell", cloudy: "Cloudy water",
  sediment: "Sediment", staining: "Staining", low_ph: "Low pH",
  iron_staining: "Iron staining", filter_blocking: "Filter blocking",
  none: "No issues",
};

/** Render a single field value into a short human string. */
export function formatQuestionnaireFieldValue(field, raw) {
  if (raw === null || raw === undefined || raw === "") return "—";
  if (field === "reason_for_testing") return REASON_LBL[raw] || String(raw);
  if (field === "primary_water_use") return PWU_LBL[raw] || String(raw);
  if (field === "filtration") return FILT_LBL[raw] || String(raw);
  if (
    field === "uv" || field === "calcite" ||
    field === "tank_cleaned_5y" || field === "home_older_than_10y"
  ) {
    return YN_LBL[raw] || String(raw);
  }
  if (field === "experienced_issues") {
    if (!Array.isArray(raw) || raw.length === 0) return "No issues";
    return raw.map((k) => ISSUE_LBL[k] || k).join(", ");
  }
  if (field === "tanks") {
    if (raw?.none) return "No tanks";
    const parts = [];
    if (raw?.poly) parts.push(`${raw.poly} poly`);
    if (raw?.concrete) parts.push(`${raw.concrete} concrete`);
    if (raw?.steel) parts.push(`${raw.steel} steel`);
    if (raw?.fibreglass) parts.push(`${raw.fibreglass} fibreglass`);
    return parts.length ? parts.join(", ") : "—";
  }
  return String(raw);
}

// Internal alias kept for the existing summary callers
const formatValue = formatQuestionnaireFieldValue;

/** Shallow equality good enough for our questionnaire field values. */
function eq(a, b) {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((v, i) => eq(v, b[i]));
  }
  if (typeof a === "object" && typeof b === "object" && a && b) {
    const ak = Object.keys(a), bk = Object.keys(b);
    if (ak.length !== bk.length) return false;
    return ak.every((k) => eq(a[k], b[k]));
  }
  return false;
}

/**
 * Returns an array of `{field, before, after}` describing every
 * questionnaire field that actually changed in this audit entry.
 * Combines the per-field `customer_diff` (reason_for_testing /
 * primary_water_use / *_other) with a top-level diff of the
 * property_info `before` / `after` snapshots.
 */
export function diffQuestionnaireAuditEntry(entry) {
  const out = [];
  if (!entry) return out;

  // customer-level diff
  const cdiff = entry.customer_diff || {};
  for (const [k, v] of Object.entries(cdiff)) {
    if (!eq(v?.before, v?.after)) {
      out.push({ field: k, before: v?.before, after: v?.after });
    }
  }

  // property_info diff
  const before = entry.before || {};
  const after = entry.after || {};
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  for (const k of keys) {
    if (!eq(before[k], after[k])) {
      out.push({ field: k, before: before[k], after: after[k] });
    }
  }
  return out;
}

/**
 * Returns a compact, sidebar-friendly summary string. Examples:
 *   "Primary water use changed from 'Drinking' → 'Irrigation / garden'"
 *   "3 fields changed — primary water use, UV treatment, home older than 10y"
 *   "no field changes"
 */
export function summariseQuestionnaireAuditEntry(entry) {
  const changes = diffQuestionnaireAuditEntry(entry);
  if (changes.length === 0) return "no field changes";
  if (changes.length === 1) {
    const { field, before, after } = changes[0];
    const label = FIELD_LABEL[field] || field;
    const beforeStr = formatValue(field, before);
    const afterStr = formatValue(field, after);
    const sentence = `${label} changed from “${beforeStr}” → “${afterStr}”`;
    // Capitalise first letter for sidebar polish
    return sentence.charAt(0).toUpperCase() + sentence.slice(1);
  }
  const MAX = 4;
  const labels = changes.map(({ field }) => FIELD_LABEL[field] || field);
  const shown = labels.slice(0, MAX).join(", ");
  const extra = labels.length > MAX ? ` +${labels.length - MAX} more` : "";
  return `${changes.length} fields changed — ${shown}${extra}`;
}

/**
 * Convenience: detect questionnaire-edit audit entries in any log.
 */
export const QUESTIONNAIRE_EDIT_ACTION = "property_info_edited";

export function isQuestionnaireEditEntry(entry) {
  return entry && entry.action === QUESTIONNAIRE_EDIT_ACTION;
}
